import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { fetchPlanning, fetchEquipmentList, fetchActivityDetails } from './api';

const EquipmentPlanner = () => {
  const [activities, setActivities] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayMeta, setDisplayMeta] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  
  // Refs for scroll sync
  const headerScrollRef = useRef(null);
  const contentScrollRef = useRef(null);
  const fixedScrollRef = useRef(null);
  
  // Sync horizontal scroll between header, content, and fixed scrollbar
  const handleContentScroll = (e) => {
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
    if (fixedScrollRef.current) {
      fixedScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  };
  
  const handleHeaderScroll = (e) => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
    if (fixedScrollRef.current) {
      fixedScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  };
  
  const handleFixedScroll = (e) => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  // Fetch equipment list on mount
  useEffect(() => {
    const loadEquipmentList = async () => {
      try {
        const response = await fetchEquipmentList();
        setEquipmentList(response.data || []);
      } catch (err) {
        console.error('Error loading equipment list:', err);
      }
    };
    loadEquipmentList();
  }, []);

  // Fetch planning data
  const loadPlanningData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchPlanning();
      setActivities(response.activities || []);
      setDisplayMeta(response.meta || null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching activities:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlanningData();
    
    // Optional: Set up polling for real-time updates
    const interval = setInterval(loadPlanningData, 60000);
    
    return () => clearInterval(interval);
  }, [loadPlanningData]);

  // Calculate the display range: round current time to nearest half hour, then go back 23 more hours
  const displayEndTime = useMemo(() => {
    // Always use current time, ignore API metadata for dynamic display
    const rounded = new Date(currentTime);
    const minutes = rounded.getMinutes();
    
    // Round to nearest half hour
    if (minutes < 15) {
      rounded.setMinutes(0, 0, 0);
    } else if (minutes < 45) {
      rounded.setMinutes(30, 0, 0);
    } else {
      rounded.setHours(rounded.getHours() + 1);
      rounded.setMinutes(0, 0, 0);
    }
    
    return rounded;
  }, [currentTime]);

  const displayStartTime = useMemo(() => {
    // Go back 23 hours from displayEndTime
    return new Date(displayEndTime.getTime() - 23 * 60 * 60 * 1000);
  }, [displayEndTime]);

  // Calculate current time marker position within the NOW block
  const currentTimeMarkerPosition = useMemo(() => {
    const minutesPastRoundedHour = currentTime.getMinutes();
    const roundedMinutes = displayEndTime.getMinutes();
    
    if (roundedMinutes === 0 && minutesPastRoundedHour >= 30) {
      return 0;
    } else if (roundedMinutes === 0 && minutesPastRoundedHour < 30) {
      return (minutesPastRoundedHour / 60) * 64;
    } else {
      return 0;
    }
  }, [currentTime, displayEndTime]);

  // Activity type colors
  const activityColors = {
    manufacturing: '#3b82f6',
    cleaning: '#10b981',
    maintenance: '#f59e0b',
  };

  // Use only equipment from equipment-list API (no merge with planning data)
  // Use equipment_name to match with planning activities
  const allEquipment = useMemo(() => {
    return equipmentList
      .map(eq => ({ id: eq.equipment_name, name: eq.equipment_name }))
      .sort((a, b) => {
        const nameA = a?.name != null ? String(a.name) : '';
        const nameB = b?.name != null ? String(b.name) : '';
        return nameA.localeCompare(nameB, undefined, { numeric: true });
      });
  }, [equipmentList]);

  // Generate 24 hours array in reverse (current time = 0, going back to -23)
  // Depends on displayEndTime to recalculate when time changes
  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => -i), [displayEndTime]);

  // Calculate activity position and width for each hour block
  // Match activities by equipment_name instead of equipment_id
  const getActivityBlocks = useCallback((equipmentName) => {
    const startTime = displayStartTime;
    const endTime = displayEndTime;

    return activities
      .filter(activity => {
        if (activity.equipment_name !== equipmentName) return false;
        const activityStart = new Date(activity.start_time);
        const activityEnd = new Date(activity.end_time);
        return activityStart < endTime && activityEnd > startTime;
      })
      .map(activity => {
        const activityStart = new Date(activity.start_time);
        const activityEnd = new Date(activity.end_time);
        
        const minutesFromEnd = (endTime - activityEnd) / (60 * 1000);
        const activityDuration = (activityEnd - activityStart) / (60 * 1000);
        
        const startPosition = (minutesFromEnd / 60) * 64;
        const width = (activityDuration / 60) * 64;
        
        return {
          ...activity,
          startPosition: Math.max(0, startPosition),
          width: Math.min(24 * 64, width),
        };
      });
  }, [activities, displayStartTime, displayEndTime]);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  if (loading && activities.length === 0) {
    return (
      <div style={{
        padding: '20px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        backgroundColor: '#f9fafb',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <div style={{ fontSize: '16px', color: '#6b7280' }}>Loading equipment schedule...</div>
        </div>
        <style>
          {`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
        </style>
      </div>
    );
  }

  if (error && activities.length === 0) {
    return (
      <div style={{
        padding: '20px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        backgroundColor: '#f9fafb',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '18px', fontWeight: '600', color: '#991b1b', marginBottom: '12px' }}>
            Error Loading Data
          </div>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
            {error}
          </div>
          <button
            onClick={loadPlanningData}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '20px', 
      fontFamily: 'system-ui, -apple-system, sans-serif',
      backgroundColor: '#f9fafb',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ 
        marginBottom: '24px',
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ margin: '0 0 16px 0', fontSize: '24px', fontWeight: '600' }}>
          Equipment Log
        </h1>
        
        {/* Time Range Display */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            padding: '8px 16px',
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#991b1b'
          }}>
            ← NOW: {formatTime(currentTime)}
          </div>
          <div style={{ color: '#6b7280', fontSize: '14px' }}>
            Showing last 24 hours (1-hour blocks, reverse chronological)
          </div>
        </div>
        
        <div style={{ color: '#6b7280', fontSize: '13px' }}>
          From: {formatDate(displayStartTime)} {formatTime(displayStartTime)} → To: {formatDate(displayEndTime)} {formatTime(displayEndTime)}
        </div>

        {/* Legend */}
        <div style={{ 
          display: 'flex', 
          gap: '20px', 
          marginTop: '16px',
          paddingTop: '16px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: '20px', 
              height: '20px', 
              backgroundColor: activityColors.manufacturing,
              borderRadius: '4px'
            }} />
            <span style={{ fontSize: '14px' }}>Manufacturing</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: '20px', 
              height: '20px', 
              backgroundColor: activityColors.cleaning,
              borderRadius: '4px'
            }} />
            <span style={{ fontSize: '14px' }}>Cleaning</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: '20px', 
              height: '20px', 
              backgroundColor: activityColors.maintenance,
              borderRadius: '4px'
            }} />
            <span style={{ fontSize: '14px' }}>Maintenance</span>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div style={{ 
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        position: 'relative'
      }}>
        {/* Sticky Header Row */}
        <div 
          ref={headerScrollRef}
          onScroll={handleHeaderScroll}
          className="header-scroll-hidden"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'white',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
            overflowX: 'auto',
            overflowY: 'visible'
          }}
        >
          <div style={{ 
            display: 'flex', 
            borderBottom: '2px solid #e5e7eb',
            minWidth: '1200px'
          }}>
            <div style={{ 
              width: '150px', 
              padding: '12px',
              fontWeight: '600',
              backgroundColor: '#f9fafb',
              position: 'sticky',
              left: 0,
              zIndex: 110,
              borderRight: '2px solid #e5e7eb',
              flexShrink: 0
            }}>
              Equipment
            </div>
            {hours.map(hourOffset => {
              const hourTime = new Date(displayEndTime.getTime() + hourOffset * 60 * 60 * 1000);
              return (
                <div
                  key={hourOffset}
                  style={{
                    width: '64px',
                    padding: '12px 8px',
                    textAlign: 'center',
                    fontSize: '11px',
                    fontWeight: '500',
                    backgroundColor: hourOffset === 0 ? '#fee2e2' : '#f9fafb',
                    borderRight: '1px solid #e5e7eb',
                    borderLeft: hourOffset === 0 ? '2px solid #ef4444' : 'none'
                  }}
                >
                  <div style={{ fontWeight: '600', color: hourOffset === 0 ? '#991b1b' : '#374151' }}>
                    {hourOffset === 0 ? 'NOW' : `${hourOffset}h`}
                  </div>
                  <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                    {hourTime.getHours().toString().padStart(2, '0')}:{hourTime.getMinutes().toString().padStart(2, '0')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Scrollable Equipment Rows */}
        <div 
          ref={contentScrollRef}
          onScroll={handleContentScroll}
          className="content-scroll-hidden"
          style={{ overflowX: 'auto' }}
        >
          <div style={{ minWidth: '1200px', position: 'relative' }}>
          {/* Equipment Rows */}
          {allEquipment.length === 0 ? (
            <div style={{
              padding: '48px',
              textAlign: 'center',
              color: '#6b7280'
            }}>
              No equipment data available
            </div>
          ) : (
            allEquipment.map((equipment) => {
              const activityBlocks = getActivityBlocks(equipment.id);
              
              return (
                <div
                  key={equipment.id}
                  style={{
                    display: 'flex',
                    borderBottom: '1px solid #e5e7eb',
                    position: 'relative',
                    minHeight: '50px'
                  }}
                >
                  {/* Equipment Name */}
                  <div style={{
                    width: '150px',
                    padding: '8px 12px',
                    fontWeight: '500',
                    fontSize: '13px',
                    backgroundColor: '#f9fafb',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'sticky',
                    left: 0,
                    zIndex: 10,
                    borderRight: '2px solid #e5e7eb',
                    flexShrink: 0
                  }}>
                    {equipment.name}
                  </div>

                  {/* Hour Blocks */}
                  <div style={{ position: 'relative', flex: 1, display: 'flex' }}>
                    {hours.map(hourOffset => (
                      <div
                        key={hourOffset}
                        style={{
                          width: '64px',
                          borderRight: '1px solid #e5e7eb',
                          borderLeft: hourOffset === 0 ? '2px solid #ef4444' : 'none',
                          backgroundColor: hourOffset === 0 ? '#fef2f2' : (Math.abs(hourOffset) % 2 === 0 ? '#ffffff' : '#fafafa')
                        }}
                      />
                    ))}

                    {/* Activity Blocks */}
                    {activityBlocks.map(activity => (
                      <div
                        key={activity.id}
                        style={{
                          position: 'absolute',
                          left: `${activity.startPosition}px`,
                          width: `${activity.width}px`,
                          top: '6px',
                          bottom: '6px',
                          backgroundColor: activityColors[activity.activity_type],
                          borderRadius: '3px',
                          padding: '4px 6px',
                          color: 'white',
                          fontSize: '11px',
                          overflow: 'hidden',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title={`${activity.activity_type} - ${activity.batch_number || activity.cleaning_type || 'maintenance'}`}
                      >
                        <div style={{ fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {activity.batch_number || activity.cleaning_type || ''}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}

          {/* Current Time Marker */}
          <div
            style={{
              position: 'absolute',
              left: `${150 + currentTimeMarkerPosition}px`,
              top: '0',
              bottom: '0',
              width: '2px',
              backgroundColor: '#ef4444',
              zIndex: 20,
              pointerEvents: 'none'
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '-8px',
                left: '-6px',
                width: '14px',
                height: '14px',
                backgroundColor: '#ef4444',
                borderRadius: '50%',
                border: '2px solid white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            />
          </div>
          </div>
        </div>
      </div>

      {/* Fixed Horizontal Scrollbar at Bottom */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '24px',
          backgroundColor: '#e5e7eb',
          borderTop: '2px solid #cbd5e1',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px'
        }}
      >
        <div
          ref={fixedScrollRef}
          onScroll={handleFixedScroll}
          className="visible-scrollbar"
          style={{
            flex: 1,
            overflowX: 'scroll',
            overflowY: 'hidden'
          }}
        >
          <div style={{ width: '2000px', height: '1px' }} />
        </div>
      </div>

      {/* Make scrollbar always visible */}
      <style>
        {`
          .header-scroll-hidden,
          .content-scroll-hidden {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .header-scroll-hidden::-webkit-scrollbar,
          .content-scroll-hidden::-webkit-scrollbar {
            display: none;
          }
          
          .visible-scrollbar {
            scrollbar-width: auto !important;
            scrollbar-color: #64748b #d1d5db !important;
          }
          .visible-scrollbar::-webkit-scrollbar {
            height: 16px !important;
            display: block !important;
          }
          .visible-scrollbar::-webkit-scrollbar-track {
            background: #d1d5db !important;
            border-radius: 8px !important;
          }
          .visible-scrollbar::-webkit-scrollbar-thumb {
            background: #64748b !important;
            border-radius: 8px !important;
            border: 3px solid #d1d5db !important;
          }
          .visible-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #475569 !important;
          }
        `}
      </style>

      {/* Scrollbar styles */}
      <style>
        {`
          .header-scroll-hidden,
          .content-scroll-hidden {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .header-scroll-hidden::-webkit-scrollbar,
          .content-scroll-hidden::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      
      {/* Add padding at bottom for fixed scrollbar */}
      <div style={{ height: '25px' }} />
    </div>
  );
};

export default EquipmentPlanner;
